import { lazy } from "react";
import { useParams } from "react-router-dom";
import AuthGuard from "../utils/route-guard/auth-guard";

// Dashboard routes are code-split so the landing (and every other route)
// doesn't ship the authenticated app + its heavy libs (antd, etc.) up front.
const Details = lazy(() => import("../pages/dashboard/withdrawals/details"));
const Mainlayout = lazy(() => import("../components/layout"));
const DashboardOverview = lazy(() => import("../pages/overview"));
const Settings = lazy(() => import("../pages/settings"));
const OrderDashboard = lazy(() => import("../pages/orders"));
const PaymentLink = lazy(() => import("../pages/orders/payment-link"));
const CreateOrder = lazy(() => import("../pages/create-order"));
const CreateLink = lazy(() => import("../pages/orders/create-link"));
const Withdrawals = lazy(() => import("../pages/withdrawal"));
const Analytics = lazy(() => import("../pages/analytics"));
const Order = lazy(() => import("../pages/orders/details"));
const Catalog = lazy(() => import("../pages/catalog"));
const ProductDetails = lazy(
  () => import("../pages/catalog/catalog-overview/catalog-product")
);
const CatalogForms = lazy(() => import("../pages/catalog/catalog-forms"));

interface RouteConfig {
  path: string;
  title?: string;
}

export interface RouteConfigMap {
  overview: RouteConfig;
  orders: RouteConfig;
  withdrawals: RouteConfig;
  createOrder: RouteConfig;
  withdrawalDetails: RouteConfig;
  [key: string]: RouteConfig;
}

export const routeConfig: RouteConfigMap = {
  overview: {
    path: "overview",
    title: "Overview",
  },
  orders: {
    path: "orders",
    title: "Orders",
  },
  withdrawals: {
    path: "withdrawals",
    title: "Withdrawals",
  },
  createOrder: {
    path: "create-order",
    title: "Create Order",
  },
  analytics: {
    path: "analytics",
    title: "Analytics",
  },
  withdrawalDetails: {
    path: "withdrawal/details/:id",
  },
  // Added catalog routes so the top header shows the correct title instead of defaulting to "Overview"
  catalogProduct: {
    path: "catalog/product/:id",
    title: "Catalog",
  },
  catalogCreate: {
    path: "catalog/create",
    title: "Create Catalog",
  },
  catalog: {
    path: "catalog",
    title: "Catalog",
  },
};

const OrderWrapper = () => {
  const { orderId } = useParams<{ orderId: string }>();
  return <Order orderId={orderId!} />;
};

// =======|| MAIN ROUTING ||========= //
const MainRoutes = [
  {
    path: "/",
    element: (
      <AuthGuard>
        <Mainlayout />
      </AuthGuard>
    ),
    children: [
      { path: "orders/payment-link/*", element: <PaymentLink /> },
      { path: "create-link", element: <CreateLink /> },

      {
        path: routeConfig.overview.path,
        element: <DashboardOverview />,
        handle: { title: routeConfig.overview?.title },
      },
      {
        path: routeConfig.orders.path,
        element: <OrderDashboard />,
        handle: { title: routeConfig.orders?.title },
      },
      {
        path: routeConfig.withdrawals.path,
        element: <Withdrawals />,
        handle: { title: routeConfig.withdrawals?.title },
      },
      {
        path: routeConfig.withdrawalDetails.path,
        element: <Details />,
      },
      {
        path: routeConfig.analytics.path,
        element: <Analytics />,
        handle: { title: routeConfig.analytics?.title },
      },
      {
        path: "account",
        element: <Settings />,
        handle: { title: "Account Settings" },
      },
      {
        path: "catalog",
        element: <Catalog />,
        handle: { title: "Catalog" },
      },
      {
        path: "catalog/create",
        element: <CatalogForms />,
        handle: { title: "Create Catalog" },
      },
      {
        path: "catalog/product/:id",
        element: <ProductDetails />,
        handle: { title: "Catalog" },
      },
    ],
  },

  {
    path: "orders/:orderId",
    element: (
      <AuthGuard>
        <OrderWrapper />
      </AuthGuard>
    ),
  },
  {
    path: routeConfig.createOrder.path,
    element: <CreateOrder />,
    handle: { title: routeConfig.createOrder?.title },
  },
];

export default MainRoutes;
